import React, { useContext } from "react";

import { observer } from "mobx-react";
import Image from "next/legacy/image";
import Link from "next/link";
import PropTypes from "prop-types";
import styled from "styled-components";

import ComponentContext from "../../../contexts/ComponentContext";
import { useStores } from "../../../contexts/StoreContext";
import Markdown from "../../styles/Markdown";

const JobCardWrapper = styled.div`
  margin-bottom: 3rem;
  height: fit-content;
  display: flex;
  gap: 2rem;
  @media (min-width: 992px) {
    margin-bottom: 35px;
  }
`;

const JobImage = styled.div`
  padding-top: 5px;
  position: relative;
  height: 13rem;
  width: 13rem;
  margin: auto;

  @media (min-width: 992px) {
    padding-top: 0;
    margin-left: 2rem;

    height: 15rem;
    width: 15rem;
  }
`;

const JobTitle = styled.h3`
  font-size: 28px;
  line-height: 40px;
  font-family: TradeGothicCondensed, sans-serif;
  text-transform: uppercase;
  color: #18191c;
  margin-bottom: 0.5rem;
  font-weight: 700;

  @media (min-width: 992px) {
    font-size: 22px;
    line-height: 24px;
  }
`;

const JobLink = styled.div`
  font-size: 22px;
  padding: 15px 5px;
  min-width: 150px;
  width: fit-content;
  text-transform: uppercase;
  border-radius: 2px;

  @media (min-width: 992px) {
    font-size: 15px;
    padding: 10px 5px;
    min-width: 120px;
  }
`;

const StyledDescription = styled.div`
  max-height: 7.5rem;
  overflow: hidden;

  @media (min-width: 992px) {
    font-size: 15px;
    padding: 10px 5px;
    min-width: 120px;
  }
`;

function JobCard(props) {
  const { vacancy } = props;

  const { trackingStore } = useStores();
  const { refererQuery, refererQueryString } = trackingStore;
  const { viewJobButtonText, thumbnail } = useContext(ComponentContext);

  const thumbnailUrl =
    thumbnail?.formats?.thumbnail?.url || thumbnail?.url || null;

  const imageUrl = vacancy?.company?.logoUrlFull || thumbnailUrl || "";

  let titleText = vacancy.jobTitle;

  if (vacancy?.location?.city) {
    titleText = `${vacancy.jobTitle} - ${vacancy?.location?.city}`;
  }

  const shouldAppendReferer = refererQuery?.utm_source !== "Direct";

  const vacancyHref = [
    `/job/${vacancy.id}`,
    shouldAppendReferer ? refererQueryString : null,
  ]
    .filter(Boolean)
    .join("?");

  return (
    <JobCardWrapper className="job-card">
      <div className="col-5 col-md-5 position-relative">
        <JobImage className="job-card-image">
          <Image
            alt={titleText}
            height={210}
            layout="responsive"
            objectFit="cover"
            src={imageUrl}
            width={210}
          />
        </JobImage>
      </div>
      <div className="col-7 col-md-7 d-flex justify-content-between flex-column gap-2">
        <JobTitle className="job-card-title">{titleText}</JobTitle>
        <StyledDescription>
          <Markdown>{vacancy?.description}</Markdown>
        </StyledDescription>
        <Link href={vacancyHref} passHref legacyBehavior>
          <JobLink className="btn btn-primary job-link-button">
            {viewJobButtonText || "View job"}
          </JobLink>
        </Link>
      </div>
    </JobCardWrapper>
  );
}

JobCard.defaultProps = {
  vacancy: {},
};

JobCard.propTypes = {
  vacancy: PropTypes.shape({
    company: PropTypes.shape({
      logoUrlFull: PropTypes.string,
    }),
    id: PropTypes.string,
    jobTitle: PropTypes.string,
    location: PropTypes.shape({
      city: PropTypes.string,
    }),
  }),
};

export default observer(JobCard);
